// frontend/src/components/admin/ConfirmResetAllFeedbackModal.tsx
import { useEffect, useId, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useAdminFeedbackResetAll,
  useAdminFeedbackResetPreview,
} from "@/hooks/useAdminFeedbackReset";

const REQUIRED_PHRASE = "RESET ALL FEEDBACK";

interface Props {
  open: boolean;
  onClose: () => void;
  onReset?: (deleted: number) => void;
}

export function ConfirmResetAllFeedbackModal({ open, onClose, onReset }: Props) {
  const [typed, setTyped] = useState("");
  const inputId = useId();

  // `"all"` = all-users scope when open; `null` = disabled (don't fire
  // destructive-preview requests just because the modal is mounted).
  const preview = useAdminFeedbackResetPreview(open ? "all" : null);
  const p = preview.data;

  const reset = useAdminFeedbackResetAll();

  useEffect(() => {
    if (!open) {
      setTyped("");
      reset.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (reset.isSuccess && reset.data && onReset) {
      onReset(reset.data.deleted);
    }
  }, [reset.isSuccess, reset.data, onReset]);

  const phraseMatches = typed === REQUIRED_PHRASE;

  function handleReset() {
    if (!phraseMatches) return;
    reset.mutate();
  }

  const loading = open && preview.isLoading;
  const hasRows = !!p && p.total > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="size-4" />
            Reset ALL feedback events
          </DialogTitle>
          <DialogDescription>
            This will permanently delete every feedback_event row for every
            user across the system. Cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-4 text-sm text-muted-foreground">Loading preview…</div>
        )}

        {!loading && p && !hasRows && (
          <div className="py-4 text-sm text-muted-foreground">
            No feedback events to reset.
          </div>
        )}

        {!loading && hasRows && (
          <>
            <div className="rounded border p-3 text-sm">
              <div className="font-medium mb-2">
                {p.total.toLocaleString()} feedback event
                {p.total === 1 ? "" : "s"} will be deleted
              </div>
              <ul className="space-y-0.5 text-muted-foreground">
                {p.by_signal_type.map((row) => (
                  <li key={row.signal_type} className="flex justify-between">
                    <span className="font-mono text-xs">{row.signal_type}</span>
                    <span className="tabular-nums">{row.count.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor={inputId}>
                Type <span className="font-mono font-semibold">{REQUIRED_PHRASE}</span> to enable the destructive action:
              </Label>
              <Input
                id={inputId}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={REQUIRED_PHRASE}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </>
        )}

        {reset.isError && (
          <div
            role="alert"
            className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive"
          >
            {(reset.error as Error)?.message ?? "Reset failed."}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reset.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={!hasRows || !phraseMatches || reset.isPending || loading}
          >
            {reset.isPending ? "Resetting…" : "Reset all feedback events"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
